import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { ShoppingCartDto, UserOrderStatusDto } from 'src/app/core/dtos/dto';
import { AffiliateLinkActivityTypeEnum, BrandEnum, OrderStatusEnum, ProductTypeEnum } from 'src/app/core/dtos/enum';
import { RegData } from 'src/app/core/dtos/reg-data';
import { Step } from 'src/app/core/dtos/step';
import { AffiliateService } from 'src/app/core/services/affiliate.service';
import { CheckoutService, CheckoutWorkflowEnum } from 'src/app/core/services/checkout.service';
import { GoogleAnalyticsService } from 'src/app/core/services/google-analytics.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { LoadingService } from 'src/app/core/services/loading.service';
import { PromotionLinkService } from 'src/app/core/services/promotion-link.service';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { CheckoutStepEnum, StepProgressService } from 'src/app/core/services/step-progress.service';
import { selectRegData } from 'src/app/state/reg-data.selectors';
import { loadShoppingCart } from 'src/app/state/shopping-cart.actions';
import { selectShoppingCart } from 'src/app/state/shopping-cart.selectors';


@Component({
  selector: 'app-done',
  templateUrl: './done.component.html',
  styleUrls: ['./done.component.scss']
})
export class DoneComponent implements OnInit, OnDestroy {

  lastOrderNumber: string;
  userOrderStatus: number;
  orderStatusSubscription: Subscription;
  orderNumberSubscription: Subscription;
  orderIncompleted: boolean;
  loading: boolean;
  brandId: BrandEnum;
  steps: Step[];
  shoppingCart$: Observable<ShoppingCartDto>;
  initialShoppingCart: ShoppingCartDto;
  approvedStatusNumber = OrderStatusEnum.PaymentApproved;
  reg: RegData;
  regData$: Observable<RegData>;

  constructor(
    private checkoutService: CheckoutService,
    private loadingService: LoadingService,
    private configHelper: ConfigHelper,
    private shoppingCartService: ShoppingCartService,
    private promotionLinkService: PromotionLinkService,
    private stepProgressService: StepProgressService,
    private store: Store,
    private affiliateService: AffiliateService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.steps = this.stepProgressService.getCheckoutSteps(CheckoutStepEnum.Completed);
  }

  ngOnInit(): void {
    this.loadingService.forceHide();
    this.getInitialShoppingCartInfo();
    this.checkoutService.redirectCheckoutCompleteBasedOnFlow(CheckoutWorkflowEnum.NoRegCheckout);
    this.brandId = this.configHelper.getBrandId();
    this.lastOrderNumber = this.checkoutService.getLastCheckoutOrderNumber();
    if (this.lastOrderNumber) {
      this.getOrderStatus();
    }
  }

  getInitialShoppingCartInfo() {
    this.shoppingCart$ = this.store.pipe(select(selectShoppingCart));
    this.regData$ = this.store.pipe(select(selectRegData));
    combineLatest([this.shoppingCart$, this.regData$])
      .pipe(take(1))
      .subscribe(([shoppingCart, regData]) => {
        this.initialShoppingCart = shoppingCart;
        this.reg = regData;
      });
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  getOrderStatus() {
    this.loadingService.show();
    this.loading = true;
    this.orderStatusSubscription = this.checkoutService.getLastOrderStatus(this.lastOrderNumber)
      .subscribe(
        (result) => {
          this.userOrderStatus = result.content.userOrderStatus;
          if (this.userOrderStatus === OrderStatusEnum.Pending) {
            setTimeout(() => {
              this.checkoutService.getLastOrderStatus(this.lastOrderNumber)
                .subscribe(
                  (res) => {
                    this.processResult(res.content);
                    this.userOrderStatus = res.content.userOrderStatus;
                  },
                  () => {
                    this.loadingService.hide();
                    this.loading = false;
                  });
            }, 5000);
          }
          else {
            this.processResult(result.content);
          }
        },
        () => {
          this.loadingService.hide();
          this.loading = false;
        });
  }

  processResult(result: UserOrderStatusDto) {
    this.shoppingCartService.getShoppingCart()
      .subscribe(
        res => {
          this.store.dispatch(loadShoppingCart({ shoppingCart: res.content }));
        });
    this.loadingService.hide();
    this.loading = false;
    if (result.userOrderStatus === OrderStatusEnum.PaymentApproved) {
      this.googleAnalyticsService.orderCompleteEventEmitter(result, this.initialShoppingCart, this.reg)
      // todo: check later and add ecommerce tracking if needed,
      if (this.checkoutService.getAffiliateCheckoutAvailableStatus() === 'true') {
        this.affiliateService.recordAffiliateActivty(AffiliateLinkActivityTypeEnum.Purchase, result.userAccountId, result.userOrderId).subscribe(() => {
          this.checkoutService.resetAffiliateTracking();
        });
      }
      this.promotionLinkService.cleanPromotionLinkCodeFromSession(sessionStorage);
    }
    else {
      this.orderIncompleted = true;
    }
  }

  ngOnDestroy() {
    this.orderStatusSubscription?.unsubscribe();
    this.orderNumberSubscription?.unsubscribe();
  }

}
